import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Renders authored release-note HTML inside a sandboxed iframe.
 *
 * Isolation model:
 *   - `sandbox="allow-same-origin"` (note: NO `allow-scripts`) means any
 *     <script> embedded in the authored HTML will NOT execute. We keep
 *     `allow-same-origin` so the parent can (a) auto-size the frame to its
 *     content, (b) widen the content past the authored max-width, and
 *     (c) make images openable in a lightbox — all driven from the parent,
 *     since scripts inside the frame can't run.
 *   - The iframe's own styles/layout stay contained, so authored markup can't
 *     leak into or clash with the app's Tailwind styles.
 */
export function AuthoredHtml({ html }: { html: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(320);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const resize = useCallback(() => {
    const doc = ref.current?.contentDocument;
    if (!doc?.body) return;
    const next = Math.max(
      doc.body.scrollHeight,
      doc.documentElement.scrollHeight,
    );
    if (next > 0) setHeight(next);
  }, []);

  // Wire up the framed document once it has loaded (and again whenever the
  // srcDoc changes, which fires a fresh load).
  const handleLoad = useCallback(() => {
    const doc = ref.current?.contentDocument;
    if (!doc) return;

    // Let the content use the full available width. Authored docs cap
    // `.rn-content` at a narrow max-width; override it here so the release
    // note fills the (wide) card instead of sitting in a narrow column.
    if (!doc.getElementById('rn-app-overrides')) {
      const style = doc.createElement('style');
      style.id = 'rn-app-overrides';
      style.textContent =
        '.rn-content{max-width:1200px !important;margin-left:auto !important;margin-right:auto !important;}';
      doc.head?.appendChild(style);
    }

    // Make every image openable in a lightbox.
    Array.from(doc.images).forEach((img) => {
      img.style.cursor = 'zoom-in';
      const el = img as HTMLImageElement & { _rnBound?: boolean };
      if (!el._rnBound) {
        el._rnBound = true;
        img.addEventListener('click', () =>
          setLightboxSrc(img.currentSrc || img.src),
        );
      }
      // Re-measure once late-loading images change layout height.
      if (!img.complete) img.addEventListener('load', resize, { once: true });
    });

    resize();
    // External resources (images) settle after load — re-measure a few times.
    [150, 500, 1200].forEach((ms) => window.setTimeout(resize, ms));
  }, [resize]);

  // Close the lightbox on Escape.
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxSrc(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxSrc]);

  return (
    <>
      <iframe
        ref={ref}
        title="Release note content"
        sandbox="allow-same-origin"
        srcDoc={html}
        onLoad={handleLoad}
        className="w-full rounded-xl border border-slate-200 bg-white"
        style={{ height }}
      />
      {lightboxSrc ? (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 p-4 cursor-zoom-out"
          onClick={() => setLightboxSrc(null)}
          role="dialog"
          aria-modal="true"
        >
          <img
            src={lightboxSrc}
            alt=""
            className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
          />
          <button
            type="button"
            onClick={() => setLightboxSrc(null)}
            className="absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1 text-sm font-medium text-slate-800 shadow hover:bg-white"
            aria-label="Close"
          >
            ✕ Close
          </button>
        </div>
      ) : null}
    </>
  );
}
